// One person's engagement schedule for one event, inside the attendance dialog
// (Contracts.personnel). Prepare merges the event's template with this person's
// record; Shelter Stone signs; they're emailed to sign in the app under My events.
//
// `startSignal` lets the attendance dialog start that sequence itself — it does
// when the status is moved to Contract Sent — and `onState` tells it whether
// there is anything to start.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import {
  Alert, Box, Button, CircularProgress, Dialog, DialogActions, DialogContent,
  DialogContentText, DialogTitle, Stack, TextField, Typography,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import ContractSummary from "./ContractSummary";
import ShelterStoneSignDialog from "./ShelterStoneSignDialog";
import { ACTIVE_CONTRACT_STATUSES, CONTRACTS_API } from "../contracts";
import { useWriteLock } from "../useWriteLock";
import { notify } from "../notify";

/**
 * Props: attendance ({id, event_id}), startSignal (number — prepare and sign when
 * it changes), beforeAction() → Promise<saved: boolean> (the dialog saving its
 * unsaved edits, since the merge reads the saved record),
 * onState({ active, hasTemplate }), onChanged() once a contract has
 * been signed and sent (which moves the attendance to Contract Sent).
 */
export default function PersonnelContractPanel({ attendance, startSignal, beforeAction, onState, onChanged }) {
  const [contracts, setContracts] = useState(null);
  const [hasTemplate, setHasTemplate] = useState(false);
  const [signing, setSigning] = useState(null);
  const [preparing, setPreparing] = useState(false);
  const [confirm, setConfirm] = useState(null);   // "void" | "delete" | "resend"
  const [reason, setReason] = useState("");
  const { busy, run } = useWriteLock();
  const attId = attendance?.id;
  const eventId = attendance?.event_id;

  const load = useCallback(() => {
    if (!attId) return Promise.resolve();
    return Promise.all([
      axios.get(CONTRACTS_API + "/", { params: { attendance_id: attId, kind: "personnel", include_history: true } }),
      axios.get(`${CONTRACTS_API}/engagement/${eventId}`),
    ]).then(([c, e]) => {
      setContracts(c.data || []);
      setHasTemplate(!!e.data.template_filename);
    }).catch(() => setContracts([]));
  }, [attId, eventId]);

  useEffect(() => { setContracts(null); load(); }, [load]);

  const current = (contracts || []).find(c => ACTIVE_CONTRACT_STATUSES.includes(c.status));
  const previous = (contracts || []).filter(c => c !== current);

  useEffect(() => {
    if (contracts) onState?.({ active: current || null, hasTemplate });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [contracts, hasTemplate]);

  const saveFirst = () => (beforeAction ? beforeAction() : Promise.resolve(false));

  // Merge a fresh draft (replacing any earlier draft), then go straight to signing.
  const prepare = () => saveFirst().then(merge, () => {});

  // Signing an existing draft: if the attendance had unsaved edits they're saved
  // now, and the draft — merged from the record as it was — is merged again.
  const signDraft = () => saveFirst().then(saved => (saved ? merge() : setSigning(current)), () => {});

  const merge = () => {
    setPreparing(true);
    return axios.post(CONTRACTS_API + "/personnel/prepare", { attendance_id: attId })
      .then(res => {
        const { contract, blank_fields: blanks } = res.data;
        if (blanks.length) notify(`Prepared — left blank: ${blanks.join(", ")}.`, "warning");
        setContracts(cs => [contract, ...(cs || []).filter(c => c.status !== "draft")]);
        setSigning(contract);
      })
      .catch(() => {})
      .finally(() => setPreparing(false));
  };

  useEffect(() => {
    if (startSignal) prepare();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [startSignal]);

  const doConfirm = () => {
    const req = confirm === "delete" ? axios.delete(`${CONTRACTS_API}/${current.id}`)
      : confirm === "void" ? axios.post(`${CONTRACTS_API}/${current.id}/void`, { reason: reason.trim() || null })
      : axios.post(`${CONTRACTS_API}/${current.id}/resend`);
    return run(() => req.then(() => {
      notify(confirm === "delete" ? "Draft deleted." : confirm === "void" ? "Contract voided." : "Reminder emailed.", "success");
      setConfirm(null); setReason(""); load();
    }));
  };

  if (contracts === null) return <Stack sx={{ alignItems: "center", py: 1 }}><CircularProgress size={20} /></Stack>;

  return (
    <Stack spacing={1}>
      {!hasTemplate && !current && (
        <Typography variant="body2" color="text.secondary">
          This event has no engagement schedule template yet — add one on the event (Engagement
          schedules) to send contracts from here.
        </Typography>
      )}
      {current && (
        <ContractSummary contract={current} counterpartyLabel="Contractor"
          actions={(
            <>
              {current.status === "draft" && (
                <>
                  <Button size="small" variant="contained" startIcon={<DrawIcon />}
                          disabled={preparing} onClick={signDraft}>Sign and send</Button>
                  <Button size="small" disabled={preparing} onClick={prepare}>Prepare again</Button>
                  <Button size="small" color="error" onClick={() => setConfirm("delete")}>Delete draft</Button>
                </>
              )}
              {current.status === "sent" && (
                <Button size="small" onClick={() => setConfirm("resend")}>Resend email</Button>
              )}
              {current.status === "signed" && (
                <Button size="small" variant="contained" disabled={busy}
                        onClick={() => run(() => axios.post(`${CONTRACTS_API}/${current.id}/finalise`).then(load))}>
                  Try finishing again
                </Button>
              )}
              {["sent", "signed"].includes(current.status) && (
                <Button size="small" color="error" onClick={() => setConfirm("void")}>Void</Button>
              )}
            </>
          )} />
      )}
      {!current && hasTemplate && (
        <Box>
          <Button size="small" variant="outlined" disabled={preparing}
                  startIcon={preparing ? <CircularProgress size={14} /> : <DrawIcon />} onClick={prepare}>
            {preparing ? "Preparing…" : previous.some(c => c.status === "completed")
              ? "Prepare a new engagement schedule" : "Prepare engagement schedule"}
          </Button>
        </Box>
      )}
      {previous.map(c => (
        <ContractSummary key={c.id} contract={c} counterpartyLabel="Contractor"
          actions={c.status === "completed" && (
            <Button size="small" disabled={busy} onClick={() => run(() =>
              axios.post(`${CONTRACTS_API}/${c.id}/email-copies`).then(() => notify("Signed copy emailed again.", "success")))}>
              Email copy again
            </Button>
          )} />
      ))}

      <ShelterStoneSignDialog open={signing !== null} contracts={signing ? [signing] : []}
                              recipientLabel="person" onClose={() => setSigning(null)}
                              onDone={() => { load(); onChanged?.(); }} />

      <Dialog open={confirm !== null} onClose={() => setConfirm(null)} fullWidth maxWidth="xs">
        <DialogTitle>
          {confirm === "delete" ? "Delete draft?" : confirm === "void" ? "Void this contract?" : "Resend the email?"}
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            {confirm === "delete" && "The draft is removed; nothing has been sent."}
            {confirm === "void" && "It can no longer be signed. The record is kept, and you can prepare a new one."}
            {confirm === "resend" && `${current?.signer_name} is emailed again at ${current?.signer_email}.`}
          </DialogContentText>
          {confirm === "void" && (
            <TextField label="Reason (optional)" fullWidth sx={{ mt: 2 }} value={reason}
                       onChange={e => setReason(e.target.value)} />
          )}
          {confirm === "void" && current?.stale_fields?.length > 0 && (
            <Alert severity="info" sx={{ mt: 2 }}>Then prepare a new one to pick up the changes.</Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirm(null)}>Cancel</Button>
          <Button variant="contained" color={confirm === "resend" ? "primary" : "error"} disabled={busy}
                  onClick={doConfirm}>
            {confirm === "delete" ? "Delete" : confirm === "void" ? "Void" : "Resend"}
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
